import { BookIcon, DocumentTextIcon, Help_1Icon, TableIcon } from "vega-icons";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";
import { getIsLPCoInvestPortal } from "@vega-core/utils/global";

import { useResourcesRoute } from "../../../hooks/useResourcesRoute";
import { CopyEmailButton } from "../components/CopyEmailButton";
import { ModalRoutes } from "../components/PermissionsHelpModal/constants";
import { ResourceCard } from "../components/ResourceCard";
import { SettingsFeedbackModal } from "../components/SettingsFeedbackModal";
import { SettingsSectionLayout } from "../components/SettingsSectionLayout";
import { SubmitTicketModal } from "../components/SubmitTicketModal/SubmitTicketModal";
import { usePermissionsHelp } from "../context/PermissionsHelpContext";
import { useSettingsFeedbackTasks } from "../hooks/useSettingsFeedbackTasks";

const SectionWithIcon = ({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-3">
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border text-text/primary">
        {icon}
      </div>
      <h4 className="body-base-semibold text-text/primary">{title}</h4>
    </div>
    {children}
  </div>
);

export const Support = () => {
  const isLPCoInvestPortal = getIsLPCoInvestPortal();
  const {
    contacts: { helpEmail },
  } = usePublicConfig();
  const { featureFlag } = useFeatureFlag();
  const { openWithRoute } = usePermissionsHelp();
  const resourcesRoute = useResourcesRoute();

  const isSubmitTicketModalOpen = useBoolean(false);
  const isSupportTicketSubmissionEnabled =
    featureFlag[FeatureFlag.LP_SUPPORT_TICKET_SUBMISSION];

  const { tasks, hasTasks, invalidate } = useSettingsFeedbackTasks();
  const isFeedbackModalOpen = useBoolean(false);

  const handleFeedbackModalClose = () => {
    isFeedbackModalOpen.setFalse();
    invalidate();
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <h2 className="body-base-semibold text-text/primary">
          {m.navTitles_helpCentre()}
        </h2>

        <SettingsSectionLayout bordered>
          <SectionWithIcon
            icon={<Help_1Icon className="h-5 w-5" />}
            title={m.titles_generalInquires()}
          >
            <div className="flex items-center justify-between gap-4">
              <p className="body-base-regular text-text/primary">
                {m.settings_supportContactDescription()}{" "}
                <a
                  className="text-text/interactive"
                  href={`mailto:${helpEmail}`}
                >
                  {helpEmail}
                </a>
              </p>
              <CopyEmailButton email={helpEmail} />
            </div>
            {isSupportTicketSubmissionEnabled ? (
              <ThemedButton
                dataTestId="submit-ticket-button"
                onClick={isSubmitTicketModalOpen.setTrue}
                variant="secondary"
              >
                {m.support_submitTicket_button()}
              </ThemedButton>
            ) : null}
          </SectionWithIcon>
        </SettingsSectionLayout>

        {isLPCoInvestPortal ? null : (
          <SettingsSectionLayout bordered>
            <SectionWithIcon
              icon={<BookIcon className="h-5 w-5" />}
              title={m.navTitles_resources()}
            >
              <div className="flex flex-col gap-3">
                <ResourceCard
                  description={m.settings_resourcesRolesDescription()}
                  icon={<DocumentTextIcon className="h-5 w-5" />}
                  onClick={() => openWithRoute(ModalRoutes.Roles)}
                  title={m.settings_resourcesRolesTitle()}
                />
                <ResourceCard
                  description={m.settings_resourcesPermissionsDescription()}
                  icon={<TableIcon className="h-5 w-5" />}
                  onClick={() => openWithRoute(ModalRoutes.Permissions)}
                  title={m.settings_resourcesPermissionsTitle()}
                />
                <ThemedButton
                  dataTestId="view-more-resources"
                  route={resourcesRoute}
                  variant="secondary"
                >
                  {`${m.settings_viewMoreResources()} →`}
                </ThemedButton>
              </div>
            </SectionWithIcon>
          </SettingsSectionLayout>
        )}

        {hasTasks ? (
          <SettingsSectionLayout bordered>
            <div className="flex flex-col gap-2">
              <h4 className="body-base-semibold">
                {m.settings_giveFeedbackTitle_v2()}
              </h4>
              <p className="body-base-regular text-text/secondary">
                {m.settings_giveFeedbackDescription_v2()}
              </p>
              <ThemedButton
                dataTestId="give-feedback-button"
                onClick={isFeedbackModalOpen.setTrue}
                variant="secondary"
              >
                {m.settings_giveFeedbackButton()}
              </ThemedButton>
            </div>
          </SettingsSectionLayout>
        ) : null}
      </div>

      <div className="flex flex-col gap-4">
        <h2 className="body-base-semibold text-text/primary">
          {m.settings_deactivateAccountTitleV2()}
        </h2>
        <SettingsSectionLayout bordered>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <p className="body-base-regular text-text/secondary">
              {m.settings_deactivateAccountDescriptionV2()}{" "}
              <a className="text-text/interactive" href={`mailto:${helpEmail}`}>
                {helpEmail}
              </a>
            </p>
            <CopyEmailButton email={helpEmail} />
          </div>
        </SettingsSectionLayout>
      </div>

      {isSupportTicketSubmissionEnabled ? (
        <SubmitTicketModal
          open={isSubmitTicketModalOpen.value}
          setOpen={isSubmitTicketModalOpen.setValue}
        />
      ) : null}

      <SettingsFeedbackModal
        onClose={handleFeedbackModalClose}
        open={isFeedbackModalOpen.value}
        tasks={tasks}
      />
    </>
  );
};
